import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Collapse,
} from '@mui/material';
import {
  Email as EmailIcon,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error as ErrorIcon,
  ExpandMore,
  ExpandLess,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { apiService, EmailConfig } from '../../services/ApiService';

const EmailConfigPanel: React.FC = () => {
  const [config, setConfig] = useState<EmailConfig>({
    smtp_server: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    sender_email: '',
    recipient_emails: '',
    email_enabled: false,
    min_hours_between_emails: 6,
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Charger la config quand on déplie le panel
  useEffect(() => {
    if (expanded && !configLoaded) {
      loadConfig();
    }
  }, [expanded, configLoaded]);

  
  const loadConfig = async () => {
    if (configLoaded) return;
    try {
      setLoading(true);
      const data = await apiService.getEmailConfig();
      setConfig(data);
      setConfigLoaded(true);
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement de la configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      const updated = await apiService.updateEmailConfig(config);
      setConfig(updated);
      setMessage({ type: 'success', text: 'Configuration enregistrée avec succès' });
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'enregistrement' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setMessage(null);
      const result = await apiService.testEmailConnection();
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Erreur lors du test de connexion' });
    } finally {
      setTesting(false);
    }
  };

  const handleChange = (field: keyof EmailConfig) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
      : event.target.type === 'number'
      ? parseInt(event.target.value, 10)
      : event.target.value;
    setConfig({ ...config, [field]: value });
  };

  
  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color={config.email_enabled ? 'primary' : 'disabled'} />
          <Typography variant="h6">
            Configuration des notifications email
          </Typography>
          {config.email_enabled && (
            <CheckCircle color="success" fontSize="small" />
          )}
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider sx={{ my: 2 }} />
        
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={24} />
            <Typography>Chargement...</Typography>
          </Box>
        )}
        
        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Activation */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.email_enabled}
                  onChange={handleChange('email_enabled')}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1" fontWeight="medium">
                  Activer les notifications par email
                </Typography>
              }
            />
          </Grid>

          {/* Serveur SMTP */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Serveur SMTP"
              value={config.smtp_server}
              onChange={handleChange('smtp_server')}
              placeholder="smtp.example.com"
              disabled={!config.email_enabled}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Port"
              type="number"
              value={config.smtp_port}
              onChange={handleChange('smtp_port')}
              disabled={!config.email_enabled}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.smtp_use_tls}
                  onChange={handleChange('smtp_use_tls')}
                  disabled={!config.email_enabled}
                />
              }
              label="TLS/SSL"
            />
          </Grid>

          {/* Authentification */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nom d'utilisateur SMTP"
              value={config.smtp_username}
              onChange={handleChange('smtp_username')}
              disabled={!config.email_enabled}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Mot de passe SMTP"
              type={showPassword ? 'text' : 'password'}
              value={config.smtp_password}
              onChange={handleChange('smtp_password')}
              disabled={!config.email_enabled}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Emails */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email expéditeur"
              value={config.sender_email}
              onChange={handleChange('sender_email')}
              placeholder="alerts@example.com"
              disabled={!config.email_enabled}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Emails destinataires"
              value={config.recipient_emails}
              onChange={handleChange('recipient_emails')}
              placeholder="admin@example.com, tech@example.com"
              helperText="Séparez les adresses par des virgules"
              disabled={!config.email_enabled}
            />
          </Grid>

          {/* Délai minimum */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Délai minimum entre emails (heures)"
              type="number"
              value={config.min_hours_between_emails}
              onChange={handleChange('min_hours_between_emails')}
              disabled={!config.email_enabled}
              helperText="Limite le nombre d'envois en cas d'alertes répétées"
            />
          </Grid>

          {/* Boutons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleTest}
                disabled={testing || !config.email_enabled || !config.smtp_server}
                startIcon={testing ? <CircularProgress size={20} /> : <SettingsIcon />}
              >
                {testing ? 'Test en cours...' : 'Tester la connexion'}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : null}
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Collapse>
    </Paper>
  );
};

export default EmailConfigPanel;
